// @systeme: navigation-client
// ============================================================================
// PairsForm — Page d'accueil publique (aiguillage à deux portes)
// ============================================================================
// Refonte 2026-08-23 (arbitrages FX) : les deux publics du lancement sont
// ① les futurs clients (pub → inscription à un groupe de Codev) — porte
// PREMIÈRE, ils sont plus nombreux et moins informés — et ② les alumni du
// Cecodev (certifiés MV/MR → espace alumni de l'org cecodev). Égalité
// visuelle des deux cartes, clients d'abord. Le signup utilisateur et
// l'espace organisation restent des liens discrets sous les portes.
// L'ancienne version (« Espace Organisations », chips « bientôt
// disponibles ») était périmée : les espaces existent depuis longtemps.
// ============================================================================

import { Link, Navigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import { Users, GraduationCap, LogIn, HelpCircle, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { PublicFooter } from "../components/layout/PublicFooter";

export default function LandingPage() {
  const { user, isLoading } = useAuth();

  // Si connecté, rediriger vers « Mon espace » — atterrissage universel
  // (FX 2026-09-19). L'ancien « /dashboard » aliasait /projets, réservé
  // SUPER_ADMIN/PILOTE : un participant connecté revenant sur « / »
  // tombait sur « Accès non autorisé ».
  if (user && !isLoading) {
    return <Navigate to="/mon-espace" replace />;
  }

  // Ne rien afficher pendant le chargement auth
  if (isLoading) {
    return null;
  }

  return (
    <Box className="auth-background" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          px: 2,
          py: { xs: 4, md: 6 },
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
          <Box className="hex-icon-container" sx={{ mb: 2, justifyContent: "center" }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "white" }}>
              C
            </Typography>
          </Box>
          <Typography variant="h2" className="gradient-text" sx={{ fontWeight: 700, mb: 1 }}>
            CECODEV
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
            L'application qui facilite l'intelligence collective
          </Typography>
        </Box>

        {/* Les deux portes — égalité visuelle, clients d'abord */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            width: "100%",
            maxWidth: 900,
          }}
        >
          {/* Porte 1 — rejoindre un groupe (futurs clients) */}
          <Card sx={{ boxShadow: "0 20px 60px rgba(0, 0, 0, 0.12)", display: "flex" }} className="fade-in">
            <CardContent sx={{ p: { xs: 3, md: 4 }, display: "flex", flexDirection: "column", flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <Users size={28} color="#E1512C" />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Rejoindre un groupe de Codéveloppement
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Progressez entre pairs, en petit groupe, avec un animateur
                certifié. Les inscriptions sont ouvertes.
              </Typography>

              <Stack spacing={1.5} sx={{ mt: "auto" }}>
                <Button
                  component={Link}
                  to="/catalogue/codev"
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<Users size={20} />}
                  sx={{ py: 1.5 }}
                >
                  Voir les groupes ouverts
                </Button>
                <Button
                  component={Link}
                  to="/plateforme/comment-choisir"
                  variant="text"
                  fullWidth
                  startIcon={<HelpCircle size={18} />}
                  sx={{ textTransform: "none" }}
                >
                  Comment choisir mon groupe ?
                </Button>
                <Button
                  component={Link}
                  to="/catalogue"
                  variant="text"
                  fullWidth
                  startIcon={<Sparkles size={18} />}
                  sx={{ textTransform: "none", color: "text.secondary" }}
                >
                  Me former à l'animation
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Porte 2 — Alumni du Cecodev */}
          <Card sx={{ boxShadow: "0 20px 60px rgba(0, 0, 0, 0.12)", display: "flex" }} className="fade-in">
            <CardContent sx={{ p: { xs: 3, md: 4 }, display: "flex", flexDirection: "column", flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <GraduationCap size={28} color="#4BB7A5" />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Alumni du Cecodev
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Formé au Module Vert ou au Module Rouge ? Retrouvez votre
                certification et votre espace alumni : e-learning, vidéos,
                groupes d'entraînement.
              </Typography>

              <Stack spacing={1.5} sx={{ mt: "auto" }}>
                <Button
                  component={Link}
                  to="/signup?type=alumni"
                  variant="contained"
                  color="secondary"
                  size="large"
                  fullWidth
                  startIcon={<GraduationCap size={20} />}
                  sx={{ py: 1.5 }}
                >
                  Retrouver mon espace
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                  Certification reconnue automatiquement — par votre email ou
                  par nom + date de naissance.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Dessous discret : connexion + inscription libre + organisations */}
        <Stack spacing={1} sx={{ mt: 4, alignItems: "center" }}>
          <Button
            component={Link}
            to="/login"
            variant="outlined"
            startIcon={<LogIn size={18} />}
            sx={{ textTransform: "none", px: 4 }}
          >
            Déjà un compte ? Se connecter
          </Button>
          <Typography variant="caption" color="text.secondary">
            Pas encore de compte ?{" "}
            <Link to="/signup" style={{ color: "inherit" }}>
              Créer un compte
            </Link>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Votre organisation veut déployer le Codéveloppement en interne ?{" "}
            <Link to="/contact" style={{ color: "inherit" }}>
              Contactez-nous
            </Link>
          </Typography>
        </Stack>
      </Box>
      <PublicFooter />
    </Box>
  );
}
